/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  ROUTES,
  ENTITY,
  DASHBOARD_DATA_TYPES,
  MONITOR_PAGE_URLS,
  SETUP_TAB_URLS,
  SETUP_ASSET_DETAILS_TAB_URLS,
  SUMMARY_TYPE,
  TIME_GRAINS,
  INTERVALS,
} from 'constants';

import update from 'immutability-helper';
import { CARD_TYPES, getCardVariables } from 'carbon-addons-iot-react';
import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage } from 'react-intl';

import { getHierarchyItemTypeFromHierarchyPath } from 'hierarchyUtils';
import { RESOURCE_TYPES } from 'dataConstants';

const messages = defineMessages({
  monitor: 'Monitor',
  setup: 'Setup',
  deviceType: 'Device type: ',
  device: 'Device:',
  dashboard: 'Dashboard',
  dashboardLabel: 'Dashboard: ',
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  allowedValues: 'Allowed values are ',
});

/** Generate the correct dashboardURL for a pin */
export const generateDashboardURL = (
  { entityTypeName, deviceId, summary, filterJson, dashboardMetaId },
  fromExplore,
  fullPath = false
) => {
  const base = fullPath ? `${window.location.protocol}//${window.location.host}` : '';

  const dashboardPath = summary ? `/${SUMMARY_TYPE}` : deviceId ? `/${ENTITY}/${deviceId}` : '';

  return `${base}${
    fromExplore ? ROUTES.MONITOR : ROUTES.DASHBOARD
  }/${entityTypeName}${dashboardPath}/${MONITOR_PAGE_URLS.DASHBOARDS}/${dashboardMetaId}${
    filterJson?.length > 0 ? `?${[filterJson].filter(params => params).join('&')}` : ''
  }`;
};

/**
 * Generate the correct edit dashboardURL for a pin
 * @param {Object} param
 * @param {string} param.entityTypeName
 * @param {string} entityId optional entity id for entity type dashboards
 * @param {string} param.dashboardMetaId the unique id of a dashboard
 * @param {Boolean} param.summary
 * @param {string} param.tabNumber
 * @param {string} location the current location
 */
export const generateEditDashboardURL = ({
  entityTypeName,
  entityId,
  dashboardMetaId,
  summary,
  tabNumber,
  location,
  hierarchyPath = '',
}) => {
  const itemType = getHierarchyItemTypeFromHierarchyPath(hierarchyPath);
  const isHierarchyDashboard =
    itemType && itemType !== RESOURCE_TYPES.DEVICE && itemType !== RESOURCE_TYPES.DEVICE_TYPE;
  const fromMonitor = location?.pathname?.includes(ROUTES.MONITOR);
  const fromManage = location?.pathname?.includes(ROUTES.SETUP);
  const pathnamePrefix = `${
    fromManage ? ROUTES.SETUP : fromMonitor ? ROUTES.MONITOR : ROUTES.DASHBOARD
  }/${!isHierarchyDashboard ? entityTypeName : 'hierarchies'}`;
  let editDashboardURL;
  if (summary) {
    editDashboardURL = `${pathnamePrefix}/editSummary/${dashboardMetaId}${
      tabNumber ? `/${tabNumber}` : ''
    }`;
  } else if (isHierarchyDashboard) {
    editDashboardURL = `${pathnamePrefix}${hierarchyPath}/dashboards/${dashboardMetaId}/edit`;
  } else {
    editDashboardURL = `${pathnamePrefix}/${
      entityId ? `${ENTITY}/${entityId}/` : ''
    }dashboards/${dashboardMetaId}/edit`;
  }
  return editDashboardURL;
};

/** Generate the correct dashboard breadcrumbs */
export const generateDashboardBreadcrumbs = ({
  entityTypeName,
  entityTypeUUID,
  entityId,
  location,
  dashboard,
  formatMessage,
  isSummaryDashboard = false,
}) => {
  const fromMonitor = !isEmpty(location.pathname) && location.pathname.includes(ROUTES.MONITOR);
  const fromSetup = !isEmpty(location.pathname) && location.pathname.includes(ROUTES.SETUP);
  const fromDashboard = !isEmpty(location.pathname) && location.pathname.includes(ROUTES.DASHBOARD);

  if (fromMonitor) {
    // go to device type or device dashboard on monitor page
    const monitorDashboardPath = entityId
      ? `${ROUTES.MONITOR_DEVICE_TYPE}/${entityTypeName}/${MONITOR_PAGE_URLS.DEVICES}/${entityId}/${MONITOR_PAGE_URLS.DASHBOARDS}`
      : `${ROUTES.MONITOR_DEVICE_TYPE}/${entityTypeName}/${MONITOR_PAGE_URLS.DASHBOARDS}`;

    return [
      <Link to={ROUTES.MONITOR}>{formatMessage(messages.monitor)}</Link>,
      ...(dashboard // If we came from a dashboard id our breadcrumbs should include this extra link because we might be on the page
        ? [
            <Link to={`${monitorDashboardPath}/${dashboard}`}>{`${formatMessage(
              messages.deviceType
            )} ${entityTypeName}`}</Link>,
          ]
        : [
            // if there is no dashboard id then go to asset type / asset dashboard page
            <Link to={`${monitorDashboardPath}`}>{`${formatMessage(
              messages.deviceType
            )} ${entityTypeName}`}</Link>,
          ]),
    ];
  }
  if (fromSetup) {
    return [
      <Link to={ROUTES.SETUP}>{formatMessage(messages.setup)}</Link>,
      <Link
        to={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/${entityTypeUUID}/${SETUP_ASSET_DETAILS_TAB_URLS.DASHBOARDS}`}>{`${formatMessage(
        messages.deviceType
      )} ${entityTypeName}`}</Link>,
      // if an entityId is passed, return back to that in the breadcrumb
      // TODO: once we get instance dashboard management
      ...(entityId
        ? [
            <Link
              to={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/${entityTypeUUID}/${MONITOR_PAGE_URLS.DEVICES}/${entityId}/${SETUP_ASSET_DETAILS_TAB_URLS.DASHBOARDS}`}>{`${formatMessage(
              messages.device
            )} ${entityId}`}</Link>,
          ]
        : []),
    ];
  }
  if (fromDashboard) {
    const dashboardPath = isSummaryDashboard
      ? `${ROUTES.DASHBOARD}/${entityTypeName}/${SUMMARY_TYPE}/${MONITOR_PAGE_URLS.DASHBOARDS}`
      : entityId
      ? `${ROUTES.DASHBOARD}/${entityTypeName}/${ENTITY}/${entityId}/${MONITOR_PAGE_URLS.DASHBOARDS}`
      : `${ROUTES.DASHBOARD}/${entityTypeName}/${MONITOR_PAGE_URLS.DASHBOARDS}`;

    return [
      <Link to={ROUTES.DASHBOARD}>{formatMessage(messages.dashboard)}</Link>,
      ...(dashboard // If we came from a dashboard id our breadcrumbs should include this extra link because we might be on the page
        ? [
            <Link to={`${dashboardPath}/${dashboard}`}>{`${formatMessage(
              messages.dashboardLabel
            )} ${dashboard}`}</Link>,
          ]
        : []),
    ];
  }
  // we dont know from which page we arrived so return empty breadcrumb
  return [];
};

/**
 * Formats the errors into human-readable logs
 * @param {Array<Object>} errors a list of validation errors from AJV or custom validators to format
 * @param errors[i].dataPath where the error occured
 * @param errors[i].data the value of the item that caused the error
 * @param errors[i].message the displayed error message
 * @param errors[i].params.allowedValues the values that data could be
 */
export const formatValidationErrors = errors =>
  errors && errors.length > 0
    ? errors
        .map(validationError => {
          // only capitalize messages from the JSON validator
          if (!isNil(validationError.dataPath)) {
            const dataPathString = !isEmpty(validationError.dataPath)
              ? ` at ${validationError.dataPath}`
              : '';
            const allowedValues = validationError.params?.allowedValues?.join(', ');
            return `Value \`${JSON.stringify(validationError.data)}\`${dataPathString}. ${
              validationError.message
            }.${allowedValues ? ` Allowed values are ${allowedValues}.` : ''}`;
          }
          return validationError.message;
        })
        .join('. ')
    : '';

export const formatTimeFrequency = (frequency, formatMessage) => {
  switch (frequency) {
    case 'Daily':
      return formatMessage(messages.daily);
    case 'Weekly':
      return formatMessage(messages.weekly);
    case 'Monthly':
      return formatMessage(messages.monthly);
    case 'Yearly':
      return formatMessage(messages.yearly);
    case 'Hourly':
    default:
      return formatMessage(messages.hourly);
  }
};
export const setDefaultsForValueCards = cardTemplate => ({
  ...cardTemplate,
  content: {
    ...cardTemplate.content,
    attributes:
      cardTemplate?.dataSource?.type !== DASHBOARD_DATA_TYPES.ALERT // for alert type data it always returns the metric as count
        ? cardTemplate.content.attributes
        : cardTemplate.content.attributes.map(attribute => ({
            ...attribute,
            dataSourceId: attribute.dataSourceId || 'count',
          })),
  },
});

/**
 *
 * @param {{*}} range the range object of a card, has a count, type and interval
 *
 * @return {element} the translated formatted message react element that explains the default range
 */
export const formatDefaultCardRange = range => {
  if (!range) {
    return '';
  }
  const { count: countProp, interval } = range || {
    count: -24,
    interval: INTERVALS.HOUR,
  }; // TODO: is this really the default interval
  // if type is not provided, assume it's rolling
  const isRelativeTime = range?.type === 'periodToDate';
  // See if we use the plural or not
  const count = Math.abs(countProp);
  const isMultiple = count !== 0 && count !== 1;
  switch (interval) {
    case INTERVALS.HOUR:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This hour (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} hours (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last hour (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} hours (default)" values={{ count }} />
      );
    case INTERVALS.DAY:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This day (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} days (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last day (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} days (default)" values={{ count }} />
      );
    case INTERVALS.WEEK:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This week (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} weeks (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last week (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} weeks (default)" values={{ count }} />
      );
    case INTERVALS.QUARTER:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This quarter (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} quarters (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last quarter (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} quarters (default)" values={{ count }} />
      );
    case INTERVALS.MONTH:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This month (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} months (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last month (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} months (default)" values={{ count }} />
      );
    case INTERVALS.YEAR:
      if (isRelativeTime) {
        return !isMultiple ? (
          <FormattedMessage defaultMessage="This year (default)" />
        ) : (
          <FormattedMessage defaultMessage="This {count} years (default)" values={{ count }} />
        );
      }
      return !isMultiple ? (
        <FormattedMessage defaultMessage="Last year (default)" />
      ) : (
        <FormattedMessage defaultMessage="Last {count} years (default)" values={{ count }} />
      );
    default:
      return <FormattedMessage defaultMessage="Default" />;
  }
};

export const setDefaultsForTimeSeriesCards = cardTemplate => ({
  ...cardTemplate,
  content: {
    ...cardTemplate.content,
    series:
      cardTemplate?.dataSource?.type !== DASHBOARD_DATA_TYPES.ALERT // for alert type data it always returns the metric as count
        ? cardTemplate.content.series
        : cardTemplate.content.series.map(line => ({
            ...line,
            dataSourceId: line.dataSourceId || 'count',
          })),
  },
  dataSource: {
    ...cardTemplate.dataSource,
    timeGrain: cardTemplate?.dataSource?.timeGrain || TIME_GRAINS.DAY, // default for timeseries should be day
  },
});

/**
 * Extends the attributes for a card to include any variables that weren't explicitly mentioned.
 *  Also this method filter out any data items that is not valid.
 * @param {*} card
 * @param {*} dataItems
 * @returns
 */
export const setVariablesForValidDataItem = (card, dataItems) => {
  const updatedCard = card;

  const dataSourceAttributes = updatedCard.dataSource?.attributes;
  const cardVariables = getCardVariables(updatedCard);

  cardVariables.forEach(variable => {
    // if the variable is in the dataSource already or it is deviceid/entity_id we don't need add a fetch for it's data
    if (variable !== 'deviceid' && variable !== 'entity_id' && variable !== 'timestamp') {
      // if the data item present then only add to the datasource attributes
      const matchingDataItem = dataItems.find(dataItem => dataItem.name === variable);

      if (matchingDataItem) {
        const matchingAttribute = dataSourceAttributes?.find(
          attribute => attribute.id === variable
        );

        // if the variable is in the dataSource already we don't need add
        if (!matchingAttribute) {
          if (!updatedCard?.dataSource?.attributes) {
            updatedCard.dataSource = { attributes: [] };
          }

          updatedCard.dataSource.attributes.push({
            attribute: variable,
            id: variable,
          });
        }
      }
    }
  });
  return updatedCard;
};
/**
 * Extends the attributes for a card to include any variables that weren't explicitly mentioned.  (This is just to be nice to the user.)
 *
 * Potentially we could catch this at validation time and stop them from importing card if they point to a variable
 * that the data layer is not going to return.
 */
export const setVariables = card => {
  const updatedCard = card;

  const dataSourceAttributes = updatedCard.dataSource?.attributes;
  const cardVariables = getCardVariables(updatedCard);

  cardVariables.forEach(variable => {
    // if the variable is in the dataSource already or it is deviceid/entity_id we don't need add a fetch for it's data
    if (variable !== 'deviceid' && variable !== 'entity_id' && variable !== 'timestamp') {
      const matchingAttribute = dataSourceAttributes?.find(attribute => attribute.id === variable);

      // if the variable is in the dataSource already we don't need add
      if (!matchingAttribute) {
        if (!updatedCard?.dataSource?.attributes) {
          updatedCard.dataSource.attributes = [];
        }

        updatedCard.dataSource.attributes.push({
          attribute: variable,
          id: variable,
        });
      }
    }
  });
  return updatedCard;
};

// need to migrate the old image location `image' to the new one `id`
export const setDefaultsForImageCards = updatedCard => {
  if (updatedCard?.content?.image) {
    return update(updatedCard, {
      content: { id: { $set: updatedCard.content.image }, $unset: ['image'] },
    });
  }
  return updatedCard;
};

export const setDashboardTemplateDefaults = template => ({
  ...template,
  cards: template.cards.map(card => {
    const updatedCard = setVariables(card);
    switch (updatedCard.type) {
      case CARD_TYPES.TIMESERIES:
        return setDefaultsForTimeSeriesCards(updatedCard);
      case CARD_TYPES.VALUE:
        return setDefaultsForValueCards(updatedCard);
      case CARD_TYPES.IMAGE:
        return setDefaultsForImageCards(updatedCard);
      default:
        return updatedCard;
    }
  }),
});
